import { Camera, MapPin, Upload } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useClockViewModel } from "@/pages/dashboard/employee/useClockViewModel";

export function ClockView() {
  const {
    user,
    storageDegraded,
    handleClockSubmit,
    isExempt,
    action,
    setAction,
    isClocked,
    clockAction,
    clockOutShiftContext,
    activeCostCodeName,
    customerName,
    costCodeRef,
    costCodeOpen,
    setCostCodeOpen,
    costCodeSearch,
    setCostCodeSearch,
    setActiveCostCodeId,
    setActiveCostCodeName,
    costCodeOptions,
    customerRef,
    customerOpen,
    setCustomerOpen,
    customerSearch,
    setCustomerSearch,
    setCustomerId,
    setCustomerName,
    customerOptions,
    synergyEmployee,
    task,
    setTask,
    notes,
    setNotes,
    mealsBreaks,
    setMealsBreaks,
    breakNotes,
    setBreakNotes,
    injured,
    setInjured,
    injuryNotes,
    setInjuryNotes,
    geoStatus,
    geo,
    geoError,
    acquireGeo,
    isMobile,
    nativeInputRef,
    handleNativePhoto,
    cameraStatus,
    videoRef,
    selfiePreview,
    selfieBlob,
    cameraError,
    setCameraError,
    startCamera,
    captureSelfie,
    selfieFromLiveCamera,
    punchError,
    punchSaved,
    punchSubmitting,
  } = useClockViewModel();

  return (
    <div className="mt-4 grid grid-cols-1 gap-4 [&>*]:min-w-0">
              <Card className="min-w-0">
                <CardHeader>
                  <CardTitle>Clock</CardTitle>
                  <CardDescription>{user?.full_name} • {user?.company_name}{/*{user?.qb_payroll_code ? ` • Payroll: ${user.qb_payroll_code}` : ""}*/}</CardDescription>
                </CardHeader>
                <CardContent>
                  <form id="clockForm" className="grid gap-4" onSubmit={handleClockSubmit}>
                    {!isExempt && (
                      <div className="grid gap-2">
                        <div className="text-sm font-medium leading-none">Action</div>
                        <div className="grid gap-2">
                          <div className="grid grid-cols-2 gap-2">
                            <Button type="button" data-testid="clock-in-btn" variant={action === "clock_in" ? "default" : "outline"} onClick={() => setAction("clock_in")}>
                              Clock In
                            </Button>
                            <Button type="button" data-testid="clock-out-btn" variant={action === "clock_out" ? "default" : "outline"} disabled={!isClocked} onClick={() => setAction("clock_out")}>
                              Clock Out
                            </Button>
                          </div>
                          <div className="text-xs text-muted-foreground">
                            Current state: {isClocked ? "Clocked in" : "Clocked out"}
                          </div>
                        </div>
                      </div>
                    )}
                    {clockAction === "clock_out" ? (
                      <>
                        <div className="grid gap-2">
                          <div className="text-sm font-medium leading-none">Cost Code</div>
                          <div className="rounded border bg-muted px-3 py-2 text-sm text-muted-foreground">
                            {clockOutShiftContext?.costCodeName || activeCostCodeName || "—"}
                          </div>
                          <div className="text-[10px] text-muted-foreground">From the matched clock-in</div>
                        </div>
                        <div className="grid gap-2">
                          <div className="text-sm font-medium leading-none">Site / Customer</div>
                          <div className="rounded border bg-muted px-3 py-2 text-sm text-muted-foreground">
                            {clockOutShiftContext?.customerName || customerName || "—"}
                          </div>
                          <div className="text-[10px] text-muted-foreground">From the matched clock-in</div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="grid gap-2">
                          <div className="text-sm font-medium leading-none">Cost Code <span className="text-destructive">*</span></div>
                          <div className="relative" ref={costCodeRef}>
                            <Input
                              value={costCodeOpen ? costCodeSearch : activeCostCodeName}
                              onChange={(e) => {
                                setCostCodeSearch(e.target.value);
                                if (!costCodeOpen) setCostCodeOpen(true);
                              }}
                              onFocus={() => {
                                setCostCodeSearch("");
                                setCostCodeOpen(true);
                              }}
                              placeholder={costCodeOpen ? "Search cost codes..." : "Select cost code"}
                            />
                            {costCodeOpen && (
                              <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md border bg-popover shadow-lg">
                                {costCodeOptions.length === 0 ? (
                                  <div className="px-3 py-2 text-sm text-muted-foreground">No results.</div>
                                ) : (
                                  costCodeOptions.map((cc) => (
                                    <button
                                      type="button"
                                      key={cc.id}
                                      className="w-full text-left cursor-pointer px-3 py-2 text-sm hover:bg-muted bg-transparent border-0"
                                      onClick={() => {
                                        setActiveCostCodeId(String(cc.id));
                                        setActiveCostCodeName(cc.cost_code);
                                        setCostCodeOpen(false);
                                        setCostCodeSearch("");
                                      }}
                                    >
                                      {cc.cost_code} {cc.general_ledger ? `(${cc.general_ledger})` : ""}
                                    </button>
                                  ))
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="grid gap-2">
                          <div className="text-sm font-medium leading-none">Site / Customer <span className="text-destructive">*</span></div>
                          <div className="relative" ref={customerRef}>
                            <Input
                              value={customerOpen ? customerSearch : customerName}
                              onChange={(e) => {
                                setCustomerSearch(e.target.value);
                                if (!customerOpen) setCustomerOpen(true);
                              }}
                              onFocus={() => {
                                setCustomerSearch("");
                                setCustomerOpen(true);
                              }}
                              placeholder={customerOpen ? "Search customers..." : "Select customer"}
                              required
                            />
                            {customerOpen && (
                              <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md border bg-popover shadow-lg">
                                {customerOptions.length === 0 ? (
                                  <div className="px-3 py-2 text-sm text-muted-foreground">No results.</div>
                                ) : (
                                  customerOptions.map((c) => (
                                    <button
                                      type="button"
                                      key={c.id}
                                      className="w-full text-left cursor-pointer px-3 py-2 text-sm hover:bg-muted bg-transparent border-0"
                                      onClick={() => {
                                        setCustomerId(String(c.id));
                                        setCustomerName(c.company);
                                        setCustomerOpen(false);
                                        setCustomerSearch("");
                                      }}
                                    >
                                      {c.company} {c.customer_id ? `(${c.customer_id})` : ""}
                                    </button>
                                  ))
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                    <div className="grid gap-2">
                      <Label htmlFor="synergy_employee_name">Synergy employee</Label>
                      <Input id="synergy_employee_name" value={synergyEmployee} readOnly disabled required className="bg-muted opacity-100 cursor-not-allowed" />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="task">Task</Label>
                      <Input id="task" value={task} onChange={(event) => setTask(event.target.value)} placeholder="Fiber pull, repair, install..." />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="notes">Notes</Label>
                      <Textarea id="notes" value={notes} onChange={(event) => setNotes(event.target.value)} />
                    </div>
                    {clockAction === "clock_out" && (
                      <div className="grid gap-3 rounded-lg border p-3">
                        <div className="grid gap-2">
                          <Label htmlFor="meals_breaks">Did you take all your meals and breaks? <span className="text-destructive">*</span></Label>
                          <Select value={mealsBreaks} onValueChange={setMealsBreaks}>
                            <SelectTrigger id="meals_breaks">
                              <SelectValue placeholder="Select..." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Yes">Yes</SelectItem>
                              <SelectItem value="No">No</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {mealsBreaks === "No" && (
                          <div className="grid gap-2">
                            <Label htmlFor="break_notes">Why did you skip breaks? <span className="text-destructive">*</span></Label>
                            <Textarea id="break_notes" value={breakNotes} onChange={(event) => setBreakNotes(event.target.value)} required />
                          </div>
                        )}
                        <div className="grid gap-2">
                          <Label htmlFor="injured">Were you or anyone injured? <span className="text-destructive">*</span></Label>
                          <Select value={injured} onValueChange={setInjured}>
                            <SelectTrigger id="injured">
                              <SelectValue placeholder="Select..." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Yes">Yes</SelectItem>
                              <SelectItem value="No">No</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {injured === "Yes" && (
                          <div className="grid gap-2">
                            <Label htmlFor="injury_notes">Please describe the injury <span className="text-destructive">*</span></Label>
                            <Textarea id="injury_notes" value={injuryNotes} onChange={(event) => setInjuryNotes(event.target.value)} required />
                          </div>
                        )}
                      </div>
                    )}
                    <div className="grid gap-2">
                      <div className="text-sm font-medium leading-none">GPS capture <span className="text-destructive">*</span></div>
                      <div className="rounded-lg border p-3">
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <div className="flex items-center gap-2 text-sm font-medium">
                              <MapPin className="size-4" />
                              Capture location
                            </div>
                            <div className="mt-1 break-all text-xs text-muted-foreground">
                              {geoStatus === "ok" && geo
                                ? `${geo.lat}, ${geo.lon} ±${Math.round(geo.accuracy)}m`
                                : "GPS is required before submit."}
                            </div>
                          </div>
                          <Badge variant="secondary">Required</Badge>
                        </div>
                        <Button
                          className="mt-3 w-full"
                          type="button"
                          variant="outline"
                          onClick={() => {
                            // Prefer a fresh fix, but fall back for Safari/Vivaldi desktop.
                            void acquireGeo({ preferFresh: true });
                          }}
                          disabled={geoStatus === "getting"}
                        >
                          {geoStatus === "getting" ? "Capturing..." : "Capture GPS"}
                        </Button>
                        {geoError && (
                          <div className="mt-2 text-xs text-destructive">{geoError}</div>
                        )}
                      </div>
                    </div>
                    <div className="grid gap-2">
                      <div className="text-sm font-medium leading-none">Photo Verification <span className="text-destructive">*</span></div>
                      <div className="rounded-lg border p-3">
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <div className="flex items-center gap-2 text-sm font-medium">
                              <Camera className="size-4" />
                              {isMobile ? "Device camera" : "Live camera preview"}
                            </div>
                            <div className="mt-1 text-xs text-muted-foreground">
                              {cameraStatus === "captured" ? "Photo captured." : "Camera photo is required before submit."}
                            </div>
                          </div>
                          <Badge variant="secondary">Required</Badge>
                        </div>
                        {isMobile && (
                          <input
                            ref={nativeInputRef}
                            type="file"
                            accept="image/*"
                            capture="user"
                            className="hidden"
                            aria-label="Upload selfie photo"
                            onChange={handleNativePhoto}
                          />
                        )}
                        {!isMobile && (
                          <video
                            ref={videoRef}
                            aria-label="Camera preview"
                            className={cameraStatus === "ready" || cameraStatus === "starting" ? "mt-3 aspect-video w-full rounded-lg border bg-black object-cover" : "hidden"}
                            autoPlay
                            muted
                            playsInline
                          />
                        )}
                        {selfiePreview ? (
                          <img src={selfiePreview} alt="Captured preview" className="mt-3 aspect-video w-full rounded-lg border object-cover" />
                        ) : selfieBlob ? (
                          <div className="mt-3 rounded-lg border bg-muted p-3 text-xs text-muted-foreground">
                            Photo captured. Preview is unavailable on this device.
                          </div>
                        ) : null}
                        {cameraError && <div className="mt-2 text-xs text-destructive">{cameraError}</div>}
                        <div className="mt-3 grid grid-cols-2 gap-2">
                          {isMobile ? (
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => {
                                setCameraError("");
                                nativeInputRef.current?.click();
                              }}
                            >
                              {cameraStatus === "captured" ? "Retake" : "Take Photo"}
                            </Button>
                          ) : (
                            <>
                              <Button type="button" variant="outline" onClick={startCamera} disabled={cameraStatus === "starting"}>
                                {cameraStatus === "captured" ? "Retake" : cameraStatus === "starting" ? "Starting..." : "Open Camera"}
                              </Button>
                              <Button type="button" onClick={captureSelfie} disabled={cameraStatus !== "ready"}>
                                Capture Photo
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    {(() => {
                      const missing: string[] = [];
                      if (!selfieBlob) missing.push("Selfie photo");
                      if (!isMobile && !selfieFromLiveCamera && selfieBlob) {
                        // Defensive: if somehow set without live capture on desktop
                        missing.push("Live webcam capture (file uploads not allowed on desktop)");
                      }
                      if (!geo) missing.push("GPS location");
                      if (clockAction !== "clock_in" && !isClocked) missing.push("Must be clocked in");
                      if (clockAction === "clock_out") {
                        if (!mealsBreaks) missing.push("Meals/breaks answer");
                        if (!injured) missing.push("Injury answer");
                        if (mealsBreaks === "No" && !breakNotes.trim()) missing.push("Break notes");
                        if (injured === "Yes" && !injuryNotes.trim()) missing.push("Injury notes");
                      }
                      return missing.length > 0 ? (
                        <div className="text-xs text-destructive">
                          Waiting on: {missing.join(", ")}
                        </div>
                      ) : null;
                    })()}
                    {punchError && (
                      <Alert role="alert" className="border-destructive/20 bg-destructive/10 text-destructive">
                        <AlertTitle>Clock submission failed</AlertTitle>
                        <div className="text-sm">{punchError}</div>
                      </Alert>
                    )}
                    {punchSaved && (
                      <Alert role="status" aria-live="polite" className="border-success/20 bg-success/10 text-success">
                        <AlertTitle>Time-clock recorded</AlertTitle>
                        <div className="text-sm">
                          {punchSaved.offline
                            ? "Your punch was saved on this device with its exact time, location, and photo. It will sync automatically when you're back online."
                            : punchSaved.direct
                            ? "Your punch was sent directly to the server (local storage was unavailable on this device)."
                            : "Your punch was saved and is syncing to the server."}
                        </div>
                      </Alert>
                    )}
                    {/* Mobile-visible: sits just above Clock In/Out so techs see it without scrolling to page top. */}
                    {storageDegraded && (
                      <Alert
                        role="alert"
                        className="border-destructive/20 bg-destructive/10 text-destructive"
                        data-testid="storage-degraded-banner"
                      >
                        <AlertTitle>Limited browser storage</AlertTitle>
                        <AlertDescription className="text-sm text-destructive">
                          Private browsing or restricted storage detected — use normal browsing (or Add to Home Screen).
                          Offline punches won&apos;t be saved. You can still clock in/out while online.
                        </AlertDescription>
                      </Alert>
                    )}
                    <Button type="submit" data-testid="punch-submit" disabled={punchSubmitting || !selfieBlob || !geo || (clockAction !== "clock_in" && !isClocked) || (clockAction === "clock_out" && (!mealsBreaks || !injured || (mealsBreaks === "No" && !breakNotes.trim()) || (injured === "Yes" && !injuryNotes.trim())))}>
                      <Upload className="size-4" />
                      {punchSubmitting ? "Saving..." : clockAction === "clock_in" ? "Clock In" : "Clock Out"}
                    </Button>
                  </form>
                </CardContent>
              </Card>

    </div>
  );
}
